import React from 'react';
import {
  Send, X, Building2, RefreshCw,
} from 'lucide-react';

/**
 * Transfer Funds modal — pick a linked bank account and enter an amount.
 * Parent owns state and submit handler.
 */
export default function TransferFundsModal({
  balance,
  bankAccounts,
  selectedAccount,
  setSelectedAccount,
  transferAmount,
  setTransferAmount,
  transferring,
  onTransfer,
  onClose,
  onGotoAddAccount,
}) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Send className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Transfer Funds</h3>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="mt-2 text-white/80">Available: ${balance.toFixed(2)}</p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">Select Bank Account</p>
            {bankAccounts.length === 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                <p className="text-amber-700 text-sm">No bank accounts added</p>
                <button
                  type="button"
                  onClick={onGotoAddAccount}
                  className="mt-2 text-amber-700 underline text-sm"
                >
                  Add a bank account first
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {bankAccounts.map((account) => (
                  <button
                    key={account._id}
                    type="button"
                    onClick={() => setSelectedAccount(account._id)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition ${
                      selectedAccount === account._id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-medium">{account.bank_name}</p>
                        <p className="text-sm text-gray-500">•••• {account.account_number.slice(-4)}</p>
                      </div>
                      {account.is_primary && (
                        <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                          Primary
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="transfer-amount-input" className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                id="transfer-amount-input"
                type="number"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                placeholder="0.00"
                min="0.01"
                step="0.01"
                max={balance}
                data-testid="transfer-amount-input"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={onTransfer}
            disabled={transferring || !selectedAccount || !transferAmount || bankAccounts.length === 0}
            className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="confirm-transfer-btn"
          >
            {transferring ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Transfer Funds
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
